<template>
  <CloseHeader :title="`${t('label_user_management')}`" />

  <div class="app-container">
    <div class="top-user-id">
      <p>{{ t('organization_id') }}</p>
      <div class="id-box">{{ teamId }}</div>
    </div>

    <div class="us-tit-wrap">
      <h1 class="app-page-tit">{{ t('label_extra_users') }}</h1>
      <div
        ref="helpContainerRef"
        v-click-outside="handleClickOutside"
        class="help-pop-con"
        :class="{ open: isHelpVisible }"
      >
        <button type="button" class="help-btn" @click="toggleHelp">
          <img :src="getAssetImageUrl('common/icons/help.svg')" alt="help" />
        </button>
        <div class="help-pop">
          <span>{{ t('description_additional_user_register') }}</span>
        </div>
      </div>
    </div>

    <div class="method-wrap method01">
      <span class="badge3">{{ t('method_additional_user_login', { method: 1 }) }}</span>
      <p>{{ t('description_additional_user_login_link') }}</p>
      <div class="link-input">
        <span>{{ subUserUrl }}</span>
        <button type="button" class="s-btn primary-bg-btn" @click="copySubUserUrl">
          {{ t('label_copy') }}
        </button>
      </div>
    </div>

    <div class="method-wrap method02">
      <span class="badge3">{{ t('method_additional_user_login', { method: 2 }) }}</span>
      <p>{{ t('description_additional_user_login') }}</p>
      <div class="inr">
        <div class="img">
          <img :src="getAssetImageUrl('app/guides/method02.png')" alt="" />
        </div>
        <button type="button" class="gray-btn l-btn" @click="goToUserAdd">
          <span>{{ t('label_add_user') }}</span>
        </button>
      </div>
    </div>

    <AppDivider />

    <div class="save-info">
      <div v-if="subUserList.length === 0" class="no-data">
        <div class="icon-area">
          <img :src="getAssetImageUrl('app/status/no-data.svg')" alt="" />
        </div>
        <span>{{ t('not_found_additional_user') }}</span>
      </div>

      <div v-else class="user-data">
        <h2>{{ t('user_information') }}</h2>
        <ul class="user-info-wrap">
          <li v-for="user in subUserList" :key="user.userId">
            <div class="user-info">
              <dl>
                <dt>ID</dt>
                <dd>{{ user.loginId }}</dd>
              </dl>
              <dl>
                <dt>{{ t('label_name') }}</dt>
                <dd>{{ user.nickname }}</dd>
              </dl>
            </div>
            <button type="button" class="line-btn round-btn" @click="goToUserAdd(user)">
              <span>{{ t('label_edit_short') }}</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { fetchEmployeeList } from '@/api/apiService';
import AppDivider from '@/components/domains/mobile/common/AppDivider.vue';
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import { usePopupStore } from '@/stores/popup';
import { useUserStore } from '@/stores/user';
import { IResponsePartnerEmployee } from '@/types/response';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

const { t } = useI18n();
const router = useRouter();

const popupStore = usePopupStore();
const userStore = useUserStore();

const isHelpVisible = ref(false);
const helpContainerRef = useTemplateRef('helpContainerRef');
const subUserList = ref<Array<IResponsePartnerEmployee>>([]);

const teamId = computed(() => userStore.userInfo.teamId);

const subUserUrl = computed(() => {
  const teamId = userStore.userInfo.teamId;
  const origin = window.origin;

  return `${origin}/login/ss?partnerCode=${teamId}`;
});

const toggleHelp = () => {
  isHelpVisible.value = !isHelpVisible.value;
};

const handleClickOutside = () => {
  isHelpVisible.value = false;
};

const copySubUserUrl = () => {
  navigator.clipboard.writeText(subUserUrl.value);
  popupStore.openToast(t('copied'));
};

const goToUserAdd = (item) => {
  router.push({
    name: 'app-team-user-manage',
    query: item ? { userId: item.userId, loginId: item.loginId, nickname: item.nickname } : {},
  });
};

const fetchSubUserList = async (offset?: string) => {
  try {
    const { data: employeeList } = await fetchEmployeeList(offset, null, true);

    if (!employeeList || employeeList.datas.length === 0) {
      subUserList.value = [];
      return;
    }

    if (offset) {
      subUserList.value.push(...employeeList.datas);
    } else {
      subUserList.value = employeeList.datas;
    }
  } catch (error) {
    console.error('추가 사용자 목록 조회 실패: ', error);
  }
};

onMounted(async () => {
  await fetchSubUserList();
});
</script>

<style scoped lang="scss">
.top-user-id {
  text-align: center;
  p {
    font-size: $font-size-psmall;
  }
  .id-box {
    background: $gray10;
    font-size: $font-size-psmall;
    padding: 0.375rem 0.75rem;
    border-radius: 100px;
    margin-top: $size-xs;
    display: inline-block;
  }
}
.top-user-id {
  text-align: center;
  p {
    font-size: $font-size-psmall;
  }
  .id-box {
    background: $gray10;
    font-size: $font-size-psmall;
    padding: 0.375rem 0.75rem;
    border-radius: 100px;
    margin-top: $size-xs;
    display: inline-block;
  }
}
.us-tit-wrap {
  display: flex;
  align-items: center;
  gap: $size-xs;
  width: 100%;
  padding: 1.5rem 0;
}
.help-pop-con {
  position: relative;
  width: 100%;
  .help-btn {
    padding: 0.25rem;
  }
  .help-pop {
    position: absolute;
    top: calc(100% + 10px);
    left: -90px;
    background: #fff;
    padding: 0.75rem 1rem;
    border-radius: $size-s;
    box-shadow: $shadow-big;
    transform: scale(0.1);
    opacity: 0;
    transition: all 0.3s ease;
    span {
      line-height: 130%;
      font-size: $font-size-psmall;
      word-break: keep-all;
      color: $color-gray-500;
    }
  }
  &.open {
    .help-pop {
      opacity: 1;
      transform: scale(1);
    }
  }
}

.method-wrap {
  p {
    font-size: 1rem;
    font-weight: 600;
    line-height: 130%;
    padding: 0.75rem 0 1rem;
  }
  &.method01 {
    .badge3 {
      color: #0078fb;
      background: #e9f4ff;
    }
  }
  &.method02 {
    margin-top: 2.5rem;
    .badge3 {
      color: #c25b00;
      background: #fff7e8;
    }
    .inr {
      .img {
        width: calc(100dvw - 40px);
        aspect-ratio: 335 / 220;
        margin-bottom: 1rem;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }
    }
  }
}

.save-info {
  padding: 1rem 0;
  .user-data {
    h2 {
      font-size: $font-size-l;
      margin-bottom: $size-m;
    }
    .user-info-wrap {
      border-top: 1px solid $color-gray-900;
      li {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid $color-gray-40;
        padding: 1rem 0;
        dl {
          display: flex;
          gap: $size-s;
          dt {
            min-width: 2.5rem;
            color: $color-gray-200;
            font-size: $font-size-psmall;
          }
          dd {
            font-size: $font-size-psmall;
          }
        }
      }
    }
  }
}

.icon-area {
  margin-bottom: $size-m;
}
.link-input {
  gap: $spacing2;
  span {
    direction: rtl;
    text-align: left;
  }
}
</style>
